import React from "react"
import { theme } from "../../../utils/theme"
import StepStatusIndicator from "components/carrotplans/StepStatusIndicator"
import { Box, Heading, Link, Stack, Text, Tooltip } from "@chakra-ui/react"
import styled from "@emotion/styled"
import { convertFromUtcToLocal } from "../../../utils/convertFromUtcToLocal"
import { CarrotPlanStepMenuItem } from "components/carrotplans/hooks/useCarrotPlanStepMenuItems"
import { Employee } from "types/Employee"
import { LegacyChip } from "../../../components/carrotplans/Chip"

const TooltipTextContainer = styled(Box)`
  font-size: 12px;
  font-weight: normal;
`
const TooltipDateText = styled(Text)`
  font-weight: bold;
`

export const CPAssistantChip = styled(LegacyChip)`
  background: #fcf3e5;
  color: #c97500;
  margin-right: 5px;
`

const CompletedDate = ({ completedAtUtc }: { completedAtUtc: string }): JSX.Element => (
  <TooltipTextContainer>
    Checked off on...
    <TooltipDateText>{convertFromUtcToLocal(completedAtUtc)}</TooltipDateText>
  </TooltipTextContainer>
)

const NotRelevantDate = ({ markedNotRelevantAtUtc }: { markedNotRelevantAtUtc: string }): JSX.Element => (
  <TooltipTextContainer>
    Marked as &quot;not relevant&quot; on...
    <TooltipDateText>{convertFromUtcToLocal(markedNotRelevantAtUtc)}</TooltipDateText>
  </TooltipTextContainer>
)

const tooltipContent = (completedAtUtc, markedNotRelevantAtUtc): JSX.Element =>
  completedAtUtc && markedNotRelevantAtUtc ? (
    <Stack direction="column" spacing="12px">
      <CompletedDate completedAtUtc={completedAtUtc} />
      <NotRelevantDate markedNotRelevantAtUtc={markedNotRelevantAtUtc} />
    </Stack>
  ) : (
    (completedAtUtc && <CompletedDate completedAtUtc={completedAtUtc} />) ||
    (markedNotRelevantAtUtc && <NotRelevantDate markedNotRelevantAtUtc={markedNotRelevantAtUtc} />) ||
    null
  )

const StepContainer = styled(Stack)`
  width: 623px;
  color: white;
  background-color: #15415b;
  border-radius: 8px;
  padding: 32px 16px 20px 16px;
`

type PlanStepsProps = {
  carrotPlanStepMenuItems: CarrotPlanStepMenuItem[]
  employee: Employee
}

type ItemDisplayProps = {
  displayText: string
  mediaText: string
  mediaEntityId: string
}

function getStepMenuItemDisplayText({ displayText, mediaText, mediaEntityId }: ItemDisplayProps): string {
  if (mediaText) {
    let replacement = mediaText
    if (!replacement.startsWith("all ")) {
      replacement = "“" + replacement + "”"
    }
    return displayText?.replace(/"\[(.+?)]"/g, replacement)
  } else if (mediaEntityId && !mediaText) {
    return "Check out our Carrot Academy resources for more information about your journey"
  }
  return displayText
}

export const PlanSteps = ({ employee, carrotPlanStepMenuItems }: PlanStepsProps): JSX.Element => (
  <StepContainer direction="column">
    <Heading fontSize="18px" fontWeight="normal" mb="12px">
      {employee.preferredName ? employee.preferredName : employee.firstName}’s Carrot Plan
    </Heading>
    <Stack direction="column" spacing="5">
      {carrotPlanStepMenuItems?.map(
        (
          {
            displayText,
            mediaText,
            mediaEntityId,
            displayUrl,
            completedAtUtc,
            markedNotRelevantAtUtc,
            isAssistantRecommended
          },
          i
        ) => (
          <Tooltip
            hasArrow
            bg={theme.colors.tooltipBlack}
            padding="12px"
            borderRadius="8"
            key={`step-${i}`}
            label={tooltipContent(completedAtUtc, markedNotRelevantAtUtc)}
          >
            <Stack direction="row" spacing="3">
              <StepStatusIndicator isComplete={!!completedAtUtc} />
              <Stack direction="column" spacing="0" overflow="hidden" opacity={markedNotRelevantAtUtc && ".5"}>
                <Text fontSize="16px" lineHeight="1.5" fontWeight="medium">
                  {isAssistantRecommended && <CPAssistantChip>CP Assistant </CPAssistantChip>}
                  {getStepMenuItemDisplayText({ displayText, mediaText, mediaEntityId })}
                </Text>
                {displayUrl ? (
                  <Link
                    textDecor="underline"
                    whiteSpace="nowrap"
                    overflowX="hidden"
                    textOverflow="ellipsis"
                    href={displayUrl}
                    target="_blank"
                  >
                    {displayUrl}
                  </Link>
                ) : (
                  <Text>--</Text>
                )}
              </Stack>
            </Stack>
          </Tooltip>
        )
      )}
    </Stack>
  </StepContainer>
)
